import { useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { Factor, FactorType } from '@supabase/supabase-js'

/** Shape returned by getAuthenticatorAssuranceLevel */
interface AssuranceLevel {
  currentLevel: 'aal1' | 'aal2'
  nextLevel: 'aal1' | 'aal2'
}

/** Shape returned by the enroll function */
interface EnrollResult {
  /** Factor ID assigned by Supabase */
  id: string
  /** Data URI of the QR code to display to the user */
  qr_code: string
  /** TOTP secret for manual entry */
  secret: string
}

/**
 * All TOTP factors for the current user, including unverified ones.
 * `data.totp` from listFactors() only contains verified factors, so we
 * use `data.all` filtered by factor_type to also surface pending enrollments.
 */
type AllFactor = Factor<FactorType>

/** Stable query keys for MFA-related data */
const MFA_KEYS = {
  factors: ['mfa', 'factors'] as const,
  aal: ['mfa', 'aal'] as const,
}

/**
 * Hook that wraps Supabase Auth MFA TOTP operations.
 *
 * Provides reactive query state for enrolled factors and current AAL level
 * via React Query, plus imperative async functions for enroll/verify/unenroll.
 *
 * All mutation functions throw on error — callers are responsible for
 * catching and displaying errors via try/catch or error boundaries.
 *
 * @returns factors — React Query result for enrolled TOTP factors
 * @returns assuranceLevel — React Query result for current/next AAL
 * @returns enroll — async function to start TOTP enrollment
 * @returns verifyAndActivate — async function to complete enrollment via challenge+verify
 * @returns unenroll — async function to remove an enrolled factor
 * @returns hasMFA — true when at least one verified TOTP factor exists
 * @returns requiresMFAVerification — true when user must complete MFA challenge to reach aal2
 */
export function useMFA() {
  const queryClient = useQueryClient()

  /**
   * Fetches all TOTP factors enrolled for the current user, including unverified ones.
   *
   * `data.totp` from listFactors() only contains verified factors (typed as
   * Factor<'totp', 'verified'>[]). We use `data.all` filtered to totp factors
   * so unverified/pending enrollments are also included for the UI.
   */
  const factors = useQuery<AllFactor[]>({
    queryKey: MFA_KEYS.factors,
    queryFn: async () => {
      if (!supabase) throw new Error('Supabase not configured')

      const { data, error } = await supabase.auth.mfa.listFactors()
      if (error) throw error
      // data.all includes every factor regardless of status; filter to totp only
      return data.all.filter((f) => f.factor_type === 'totp')
    },
    enabled: !!supabase,
  })

  /**
   * Fetches the current and next Authenticator Assurance Level for the session.
   * currentLevel reflects what the current JWT proves; nextLevel indicates
   * what level is achievable given enrolled factors.
   */
  const assuranceLevel = useQuery<AssuranceLevel>({
    queryKey: MFA_KEYS.aal,
    queryFn: async () => {
      if (!supabase) throw new Error('Supabase not configured')

      const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
      if (error) throw error
      return {
        currentLevel: data.currentLevel as 'aal1' | 'aal2',
        nextLevel: data.nextLevel as 'aal1' | 'aal2',
      }
    },
    enabled: !!supabase,
  })

  /**
   * Starts TOTP enrollment for the current user.
   *
   * Before enrolling, cleans up any existing unverified factors to avoid
   * Supabase rejecting a new enrollment due to a dangling unverified factor.
   *
   * @returns EnrollResult containing the factor id, QR code data URI, and TOTP secret
   * @throws if Supabase is not configured or any API call fails
   */
  const enroll = useCallback(async (): Promise<EnrollResult> => {
    if (!supabase) throw new Error('Supabase not configured')

    // Remove any unverified factors before starting a new enrollment.
    // Supabase limits the number of pending enrollments per user.
    // We use data.all because data.totp only contains verified factors.
    const { data: listData, error: listError } = await supabase.auth.mfa.listFactors()
    if (listError) throw listError

    const unverifiedFactors = listData.all.filter(
      (f) => f.factor_type === 'totp' && f.status === 'unverified'
    )
    await Promise.all(
      unverifiedFactors.map(async (factor) => {
        const { error: unenrollError } = await supabase!.auth.mfa.unenroll({ factorId: factor.id })
        if (unenrollError) throw unenrollError
      })
    )

    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
      issuer: 'DCP',
      friendlyName: 'DCP Authenticator',
    })
    if (error) throw error

    return {
      id: data.id,
      qr_code: data.totp.qr_code,
      secret: data.totp.secret,
    }
  }, [])

  /**
   * Completes TOTP enrollment by issuing a challenge and verifying the code.
   * On success, invalidates the factors and AAL queries so the UI reflects
   * the newly activated factor and updated assurance level.
   *
   * @param factorId — ID of the factor returned by enroll()
   * @param code — 6-digit TOTP code from the authenticator app
   * @throws if Supabase is not configured or the verification fails
   */
  const verifyAndActivate = useCallback(
    async (factorId: string, code: string): Promise<void> => {
      if (!supabase) throw new Error('Supabase not configured')

      const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code })
      if (error) throw error

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: MFA_KEYS.factors }),
        queryClient.invalidateQueries({ queryKey: MFA_KEYS.aal }),
      ])
    },
    [queryClient]
  )

  /**
   * Removes an enrolled MFA factor for the current user.
   * After unenrolling, refreshes the session to downgrade the JWT from aal2 to aal1
   * if no verified factors remain. Invalidates factors and AAL queries on success.
   *
   * @param factorId — ID of the factor to remove
   * @throws if Supabase is not configured or the unenroll/refresh fails
   */
  const unenroll = useCallback(
    async (factorId: string): Promise<void> => {
      if (!supabase) throw new Error('Supabase not configured')

      const { error } = await supabase.auth.mfa.unenroll({ factorId })
      if (error) throw error

      // Refresh the session so the JWT is re-issued without the aal2 claim
      // when the last verified factor has been removed.
      const { error: refreshError } = await supabase.auth.refreshSession()
      if (refreshError) throw refreshError

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: MFA_KEYS.factors }),
        queryClient.invalidateQueries({ queryKey: MFA_KEYS.aal }),
      ])
    },
    [queryClient]
  )

  /**
   * True when the user has at least one verified TOTP factor enrolled.
   * Used to show/hide the "remove MFA" button and related UI.
   */
  const hasMFA: boolean = factors.data?.some((f) => f.status === 'verified') ?? false

  /**
   * True when the user has a verified factor (nextLevel === 'aal2') but the
   * current session has not completed MFA verification (currentLevel === 'aal1').
   * Used to gate sensitive pages behind a re-authentication prompt.
   */
  const requiresMFAVerification: boolean =
    assuranceLevel.data?.nextLevel === 'aal2' &&
    assuranceLevel.data?.currentLevel === 'aal1'

  return {
    factors,
    assuranceLevel,
    enroll,
    verifyAndActivate,
    unenroll,
    hasMFA,
    requiresMFAVerification,
  }
}
